"use client"

import { motion } from "framer-motion"
import { useInView } from "react-intersection-observer"
import { Mail, MapPin, Send, Github } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import { SectionDivider } from "./section-divider"
import { MagneticButton } from "./magnetic-button"
import { useConsent } from "./consent-provider"
import { Link } from "next-view-transitions"

export function ModernContact() {
  const { setShowModal } = useConsent()
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      })

      if (response.ok) {
        toast.success("Message sent successfully!")
        setFormData({ name: "", email: "", message: "" })
      } else {
        toast.error("Failed to send message. Please try again.")
      }
    } catch (error) {
      toast.error("An error occurred. Please try again later.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section id="contact" className="relative overflow-hidden">
      <SectionDivider variant="gradient" className="mb-16" />

      <div className="py-32 relative">
        {/* Background */}
        <div className="absolute inset-0 bg-background" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            ref={ref}
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            className="max-w-6xl mx-auto"
          >
            {/* Section header */}
            <div className="text-center mb-20 space-y-4">
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2 }}
                className="inline-block text-sm font-medium px-4 py-1.5 rounded-full bg-muted/10 border border-border text-foreground backdrop-blur-sm"
              >
                Get In Touch
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.3 }}
                className="text-4xl md:text-6xl font-bold tracking-tight"
              >
                Say Hello
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.4 }}
                className="text-lg text-muted-foreground max-w-2xl mx-auto"
              >
                Want to chat about code, projects, or just say hi? Feel free to reach out!
              </motion.p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              {/* Contact info */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.5 }}
                className="space-y-8"
              >
                <div>
                  <h3 className="text-2xl font-bold mb-6">Contact Information</h3>

                  <div className="space-y-4">
                    <motion.a
                      href="mailto:contact@horizov.site"
                      whileHover={{ x: 5 }}
                      className="flex items-center gap-4 p-4 rounded-xl glass-panel hover:border-primary/50 transition-all group"
                    >
                      <div className="p-3 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                        <Mail className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Email</p>
                        <p className="font-medium">contact@horizov.site</p>
                      </div>
                    </motion.a>

                    <motion.div
                      whileHover={{ x: 5 }}
                      className="flex items-center gap-4 p-4 rounded-xl glass-panel"
                    >
                      <div className="p-3 rounded-lg bg-secondary/10">
                        <MapPin className="w-5 h-5 text-secondary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Location</p>
                        <p className="font-medium">Germany</p>
                      </div>
                    </motion.div>
                  </div>
                </div>

                {/* Social links */}
                <div className="flex gap-4">
                  <MagneticButton strength={0.3}>
                    <a
                      href="https://github.com/kevin-b-official"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-4 rounded-xl glass-panel hover:border-primary/50 transition-all block"
                    >
                      <Github className="w-6 h-6" />
                    </a>
                  </MagneticButton>
                </div>

                {/* Quick response time */}
                <div className="p-6 rounded-2xl bg-gradient-to-br from-primary/5 to-secondary/5 border border-primary/10">
                  <h4 className="font-semibold text-lg mb-2">Quick Response</h4>
                  <p className="text-sm text-muted-foreground">
                    I typically respond within 24 hours. Looking forward to hearing about your project!
                  </p>
                </div>
              </motion.div>

              {/* Contact form */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.6 }}
              >
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-muted/5 border border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-all"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-muted/5 border border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-all"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-muted/5 border border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-all resize-none"
                      placeholder="Tell me about your project..."
                    />
                  </div>

                  <MagneticButton strength={0.15} className="w-full">
                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full px-6 py-4 bg-primary text-primary-foreground rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary/25"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          <span>Send Message</span>
                        </>
                      )}
                    </motion.button>
                  </MagneticButton>
                </form>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <div className="py-8 border-t border-border/50">
        <div className="container mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} Kevin B. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link href="/privacy-policy" className="hover:text-primary transition-colors">
              Privacy Policy
            </Link>
            <button
              onClick={() => setShowModal(true)}
              className="hover:text-primary transition-colors"
            >
              Cookie Settings
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}
